import { useEffect, useRef } from 'react';
import { clamp, prefersReducedMotion, watchReducedMotion } from './reducedMotion';

type PointerDepthOptions = {
  /** How far the pointer can push the scene, as a multiplier on the -1..1 range. */
  strength?: number;
  /** Lerp factor per frame — lower is heavier and more cinematic. */
  damping?: number;
};

/**
 * Writes damped `--px` / `--py` pointer coordinates (-1..1) onto the element so any descendant
 * can build a parallax transform from them in pure CSS. The rAF loop only runs while the values
 * are still settling, so an idle hero costs nothing.
 */
export function usePointerDepth<T extends HTMLElement>({ strength = 1, damping = 0.085 }: PointerDepthOptions = {}) {
  const ref = useRef<T>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    let detach: (() => void) | null = null;

    const bind = () => {
      let targetX = 0;
      let targetY = 0;
      let currentX = 0;
      let currentY = 0;
      let frame = 0;

      const render = () => {
        currentX += (targetX - currentX) * damping;
        currentY += (targetY - currentY) * damping;
        node.style.setProperty('--px', currentX.toFixed(4));
        node.style.setProperty('--py', currentY.toFixed(4));
        const settled = Math.abs(targetX - currentX) < 0.0008 && Math.abs(targetY - currentY) < 0.0008;
        frame = settled ? 0 : requestAnimationFrame(render);
      };

      const request = () => {
        if (!frame) frame = requestAnimationFrame(render);
      };

      const handleMove = (event: PointerEvent) => {
        if (event.pointerType === 'touch') return;
        const rect = node.getBoundingClientRect();
        targetX = clamp(((event.clientX - rect.left) / rect.width - 0.5) * 2) * strength;
        targetY = clamp(((event.clientY - rect.top) / rect.height - 0.5) * 2) * strength;
        request();
      };

      const handleLeave = () => {
        targetX = 0;
        targetY = 0;
        request();
      };

      node.addEventListener('pointermove', handleMove, { passive: true });
      node.addEventListener('pointerleave', handleLeave, { passive: true });

      return () => {
        node.removeEventListener('pointermove', handleMove);
        node.removeEventListener('pointerleave', handleLeave);
        if (frame) cancelAnimationFrame(frame);
        node.style.setProperty('--px', '0');
        node.style.setProperty('--py', '0');
      };
    };

    const sync = (reduced: boolean) => {
      detach?.();
      detach = null;
      if (reduced) {
        node.style.setProperty('--px', '0');
        node.style.setProperty('--py', '0');
        return;
      }
      detach = bind();
    };

    sync(prefersReducedMotion());
    const unwatch = watchReducedMotion(sync);

    return () => {
      unwatch();
      detach?.();
    };
  }, [damping, strength]);

  return ref;
}
